import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { open } from "../lib/maplibre";
import { esc } from "../lib/text";

// MapLibre is its own chunk, fetched by the first map that connects (docs/MAPS.md § The controller).
let maplibregl;

// The one map (docs/MAPS.md § The controller). Pages differ only in values:
//
//   radar     — many things, and a `frame` to refresh as you pan
//   one thing — no frame, so the map just sits there
//
// What it draws is a matter of zoom, and the server already decided: it
// sends `pin` targets (one point, a flyer and all) or `spot` targets (a city
// or a venue, with a count). Both carry their JSON in `data-point`, so the
// same controller draws every scale and knows nothing about any of them.
//
// A spot is a door: clicking one flies to the zoom it names, the pan reloads
// the frame, and the next scale down is what comes back.
//
// With `clouds` the map is many small thumbs on top, the rest like glow
// (docs/MAPS.md § Fun clouds): the loudest two dozen in view are thumbs that
// the zoom scales (never swaps), every pin is a point of one heatmap, and close
// in the glow clears and each is its own pin. A host that
// covers the canvas's foot says so (`pad`), and the middle moves up.
export default class extends Controller {
  static targets = ["canvas", "pin", "spot"];
  static values = {
    center: Array,
    zoom: { type: Number, default: 14 },
    // Empty: the quiet basemap for whichever theme the page is wearing.
    style: String,
    // A Mapbox pk., for a mapbox:// style. Empty: Mapbox is never asked.
    token: String,
    // A turbo frame to reload with the new bounds on pan. Empty: a still map.
    frame: String,
    // Frame the pins drawn, again whenever the list swaps them.
    fit: Boolean,
    // The opening view (center, zoom) is the one asked for: the first fit is skipped.
    keep: Boolean,
    // The loudest few in view are flyers over a glow; the rest are the glow.
    clouds: Boolean,
    // A door out: the fullscreen button becomes a link here, carrying the
    // page's query and the map's center and zoom. Unset: fullscreen.
    expand: String,
    expandLabel: String,
    // Sometimes a radar sweep turns once over the map (an easter egg).
    sweep: Boolean,
  };

  // Thumbs in a screen: every night in view, up to this many. ponytail: past
  // four dozen DOM markers a phone lags and the map is a wall again; a constant, not a value.
  static TOP = 40;
  // Of those, the loudest three wear the bigger card.
  static BIG = 3;
  // From here every party in view is its own pin at full size, and the glow is gone.
  static STREET = 15;
  // At this zoom a thumb is half size; it grows in a line to full at STREET, and keeps shrinking OUT a zoom below it.
  static FAR = 11;
  // The scale lost a zoom below FAR, down to FLOOR: 24px, the target floor (docs/MAPS.md § Fun clouds).
  static OUT = 0.2;
  static FLOOR = 0.3;
  // Room kept clear at the sides: half a popup, and a pill at 200% text, a quarter of the box at most.
  static EDGE = 180;
  // A thumb's height over its point (the big card, 7rem) and the label under it, in px (docs/MAPS.md § The fit).
  static CARD = 112;
  // A grid's row steps by the seat and this: the label's line under a card (`pitch`).
  static LABEL = 26;
  // A label hangs this far under its own card, keeps NEAR from any other card and MARGIN from the canvas's edge; the fit leaves
  // FOOT under its lowest card: the hang, a line of 20, the margin (docs/MAPS.md § Fun clouds, label air).
  static HANG = 4;
  static NEAR = 6;
  static MARGIN = 12;
  static FOOT = 36;
  // The widest ring a card wears (the 2px "now" ring): what is painted over a card past its box (`unbury`).
  static RING = 2;
  // A map shorter than this (Depois's 20dvh strip) wears the far-out half size at any zoom (docs/MAPS.md § The fit).
  static SMALL = 200;
  // A grid seat is this much of the big card: a neighbour covers a fifth of a card at the most (`pitch`).
  static SEAT = 0.8;
  // The aim: no card lies more than this much under the cards over it (`relax`): from STREET, as r5 drew it; below it, the
  // tight aim, four fifths of every card showing (docs/MAPS.md § Crowding).
  static MOST = 0.4;
  static TIGHT = 0.2;
  // The most a card may lie under, whatever else it gives up (a sampling's margin under the half that must show).
  static SOME = 0.48;
  // A card's centre stays within this many of its widths of its point (1.5, less a hair for rounding); `relax` looks
  // for its seat on RINGS rings out to it, TURNS directions each.
  static LEASH = 1.48;
  static RINGS = 12;
  static TURNS = 24;
  // Air round a card, a label and a control: the card's ring and its shadow never touch a word, nor a button.
  static AIR = 2;
  // A pin shrinking into its spot, or out of it, in this many ms.
  static FADE = 200;
  // One load in ten the green sweep turns. ponytail: the odds are a constant.
  static SWEEP_ODDS = 0.1;

  // For when the browser won't say and the server didn't either: no city in
  // the JS, the same point Findable::FALLBACK_GEOM draws (docs/MAPS.md).
  static NOWHERE = [0, 0];

  // Liberty painted every park green and every road orange, and the pins
  // drowned in it. These two are grey on purpose: the map is the ground,
  // the pins are what's on it. OpenFreeMap: no key, no cookie.
  static TILES = {
    light: "https://tiles.openfreemap.org/styles/positron",
    dark: "https://tiles.openfreemap.org/styles/dark",
  };

  // Quieter still: building footprints, footpaths, rails, road shields,
  // one-way arrows and the small streets' names, off (positron's and dark's
  // layer ids). Main roads, water and place names stay to find your way by.
  static QUIET =
    /^(building|highway[-_]path|highway-name-(minor|path)|highway-shield.*|road_shield.*|road_oneway.*|railway.*)$/;

  // MapLibre fetches only http(s); Mapbox GL's own shorthand, spelled out.
  static MAPBOX = [
    [/^mapbox:\/\/styles\//, "https://api.mapbox.com/styles/v1/"],
    [
      /^mapbox:\/\/sprites\/([^/]+\/[^/@.]+)/,
      "https://api.mapbox.com/styles/v1/$1/sprite",
    ],
    [/^mapbox:\/\/fonts\//, "https://api.mapbox.com/fonts/v1/"],
    [/^mapbox:\/\/([^?]+)$/, "https://api.mapbox.com/v4/$1.json?secure"],
  ];

  map = null;
  markers = new Map();
  pins = new Map();
  // px of the canvas's foot a host's sheet covers (`pad`, docs/MAPS.md § Padding).
  covered = 0;
  // The opening view is the one asked for (`keep`): no fit until the map has settled.
  kept = this.keepValue;
  // What waits on the next fit to land (`refit`).
  landing = [];
  // MapLibre's `load` has fired: a fit asks for the box, never for tiles or a style that is still coming (docs/MAPS.md § The fit).
  opened = false;

  async connect() {
    maplibregl = await import("maplibre-gl");
    if (!this.hasCenterValue || this.centerValue.length !== 2) {
      this.centerValue = await this.locate();
    }
    // Gone while the chunk or the position was on its way: nothing to draw on.
    if (!this.element.isConnected) return;

    this.map = open(maplibregl, {
      container: this.canvasTarget,
      style: this.tiles(),
      center: this.centerValue,
      zoom: this.zoomValue,
      attributionControl: false,
      transformRequest: this.tokenValue ? (url) => this.mapbox(url) : undefined,
    });
    // No WebGL2: the list is the page, and a blank box is not a map.
    if (!this.map) {
      this.canvasTarget.hidden = true;
      return;
    }
    if (this.covered) this.pad({ detail: { bottom: this.covered } });
    this.map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "top-right",
    );
    this.map.addControl(
      this.expandValue ? this.expander() : new maplibregl.FullscreenControl(),
    );
    // Bottom right is where a host's chat bubble floats.
    this.map.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      "bottom-left",
    );
    this.collapseCredit();
    this.map.on("style.load", () => this.quiet());
    this.map.on("move", () => this.spread());
    // A word's width is its font's: the labels are laid again when the fonts land.
    document.fonts?.ready.then(
      () => this.map && this.cloudsValue && this.lay(),
    );
    this.map.on("load", () => {
      this.opened = true;
      if (this.cloudsValue) this.showPins();
      else this.drawAll();
      // The opening view stays: no fit from the first frames, which only name the pins.
      // docs/MAPS.md § Ready: what a spec waits on, once the opening fit has landed.
      const ready = () => this.map && (this.element.dataset.mapReady = "true");
      if (this.keepValue) {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.kept = false)),
        );
        ready();
        // The host's opening ring is the wide one and a kept view frames nothing: ask once for the map's own (docs/MAPS.md § Expand).
        // (a chip the person clicked first is already on its way: its answer stands)
        if (
          this.frameValue &&
          !document.getElementById(this.frameValue)?.hasAttribute("aria-busy")
        )
          this.refresh(0);
      } else this.refit(ready);
      if (
        this.sweepValue &&
        !this.still() &&
        Math.random() < this.constructor.SWEEP_ODDS
      )
        this.sweepOnce();
    });
    // The markers are DOM and stay put through a new style; the glow goes in again with it.
    this.map.on("style.load", () => this.cloudsValue && this.mountClouds());
    if (this.cloudsValue) {
      // The zoom only scales what stands; the set changes on the move's end, never inside the gesture.
      this.map.on("zoom", () => {
        this.scale();
        this.lay();
      });
      this.map.on("moveend", () => this.showPins());
    }
    // Born hidden (a phone's Lista|Mapa switch), it had no size to fit to.
    // A resize or a rotation ends in a moveend that is no pan (`hold`).
    this.map.on("resize", () => {
      this.hold();
      // A width that changed since the last fit (a phone turned, the strip that became the fifth) is framed in its own box.
      if (this.unfit) this.refit();
      else if (this.fitWidth && this.fitWidth !== this.canvasTarget.clientWidth)
        this.refit();
      if (this.cloudsValue) this.showPins();
    });
    if (this.frameValue) {
      this.map.on("moveend", () => {
        // The opening fitBounds is not a pan: don't reload what we just framed.
        if (this.holding) return;
        this.refresh();
      });
    }

    // The theme switch flips data-theme on <html>; the tiles follow. Markers
    // are DOM, so a new style leaves them where they are.
    this.theme = new MutationObserver(() => this.map?.setStyle(this.tiles()));
    this.theme.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }

  // MapLibre opens the credit the moment the style names its sources, and
  // only a drag folds it: on a 16rem phone map that is 40px of text over the
  // ground until the first touch. Fold it once, when it first appears; the "i"
  // still toggles it (OSM's ODbL needs it reachable, not shown).
  collapseCredit() {
    const fold = () => {
      const credit = this.element.querySelector(
        ".maplibregl-ctrl-attrib.maplibregl-compact",
      );
      if (!credit) return;
      credit.classList.remove("maplibregl-compact-show");
      this.map.off("sourcedata", fold);
    };
    this.map.on("sourcedata", fold);
  }

  // A host's sheet covers the canvas's foot by `detail.bottom` px (qir's phone
  // drawer says `drawer:peek`): the pan, a flight and the next reload's centre
  // are the middle of what is left. Not a pan: the frame is the one asked for.
  pad({ detail: { bottom } }) {
    this.covered = bottom;
    if (!this.map) return;
    const was = this.holding;
    this.holding = true; // `setPadding` ends its move before it returns
    this.map.setPadding({ top: 0, left: 0, right: 0, bottom });
    this.holding = was;
    if (this.cloudsValue) this.showPins();
  }

  // The mini map's door (docs/MAPS.md § Expand): a link control in the
  // fullscreen button's place, same glyph.
  expander() {
    const group = document.createElement("div");
    group.className = "maplibregl-ctrl maplibregl-ctrl-group";
    const link = document.createElement("button");
    link.type = "button";
    link.setAttribute("role", "link");
    link.className = "maplibregl-ctrl-fullscreen";
    link.title = link.ariaLabel = this.expandLabelValue;
    const icon = document.createElement("span");
    icon.className = "maplibregl-ctrl-icon";
    icon.setAttribute("aria-hidden", "true");
    link.append(icon);
    link.addEventListener("click", () => Turbo.visit(this.door()));
    group.append(link);
    return { onAdd: () => group, onRemove: () => group.remove() };
  }

  // The expand url, the page's own query (its filters) and the map's view.
  door() {
    const url = new URL(this.expandValue, window.location.origin);
    for (const [key, value] of new URLSearchParams(window.location.search))
      url.searchParams.set(key, value);
    const { lng, lat } = this.map.getCenter();
    url.searchParams.set("lng", lng.toFixed(3));
    url.searchParams.set("lat", lat.toFixed(3));
    url.searchParams.set("zoom", Math.round(this.map.getZoom()));
    return url.toString();
  }

  tiles() {
    if (this.styleValue) return this.styleValue;
    const scheme = getComputedStyle(document.documentElement).colorScheme;
    return this.constructor.TILES[scheme === "light" ? "light" : "dark"];
  }

  // Ours only: a host's own style is drawn as its author meant.
  quiet() {
    if (this.styleValue) return;
    for (const { id } of this.map.getStyle().layers) {
      if (this.constructor.QUIET.test(id))
        this.map.setLayoutProperty(id, "visibility", "none");
    }
    // Service roads (parking aisles, rail yards) hatch whole blocks grey; the streets stay.
    if (this.map.getLayer("highway_minor")) {
      this.map.setFilter("highway_minor", [
        "all",
        this.map.getFilter("highway_minor"),
        ["!=", ["get", "class"], "service"],
      ]);
    }
    // Dark names every street in one layer; the small ones go, as positron's highway-name-minor does.
    if (this.map.getLayer("highway_name_other")) {
      this.map.setFilter("highway_name_other", [
        "all",
        this.map.getFilter("highway_name_other"),
        ["match", ["get", "class"], ["minor", "service", "track"], false, true],
      ]);
    }
  }

  // Every ask to Mapbox carries the pk.; nobody else's does.
  mapbox(url) {
    const https = this.constructor.MAPBOX.reduce(
      (at, [from, to]) => at.replace(from, to),
      url,
    );
    if (!https.startsWith("https://api.mapbox.com/")) return;

    const signed = new URL(https);
    signed.searchParams.set("access_token", this.tokenValue);
    return { url: signed.toString() };
  }

  disconnect() {
    this.theme?.disconnect();
    clearTimeout(this.relay);
    this.element.dataset.mapReady = "false";
    this.map?.remove();
    this.map = null;
    this.opened = false;
    this.markers.clear();
    this.pins.clear();
    this.spot = null;
  }

  // Turbo swaps the list as you pan, so what is drawn follows the targets in
  // and out rather than being redrawn wholesale.
  pinTargetConnected(target) {
    if (this.cloudsValue) return this.track(target);
    if (this.map?.loaded()) this.draw(target, false);
    this.refit();
  }

  spotTargetConnected(target) {
    if (this.map?.loaded()) this.draw(target, true);
  }

  pinTargetDisconnected(target) {
    if (this.cloudsValue) {
      this.pins.delete(String(this.point(target).id));
      this.queuePins();
      return;
    }
    this.erase(target);
    this.lit?.remove();
    this.refit();
  }

  spotTargetDisconnected(target) {
    this.erase(target);
    this.spread();
  }

  drawAll() {
    this.pinTargets.forEach((one) => this.draw(one, false));
    this.spotTargets.forEach((one) => this.draw(one, true));
  }

  // One frame after the last target moved, so a swapped list fits once.
  // `then` runs when that fit has landed (or there was none to make): a fit
  // cancelled by a later one hands its waiters over.
  refit(then) {
    if (then) this.landing.push(then);
    // No fit to make: the one waiting here has its answer, a pending fit keeps its own.
    if (!this.fitValue || this.kept || !this.opened)
      return then && this.landed();
    cancelAnimationFrame(this.fitting);
    this.fitting = requestAnimationFrame(() => {
      const points = this.pinTargets
        .map((one) => this.point(one).geom)
        .filter(Boolean);
      this.unfit = !this.canvasTarget.clientWidth;
      this.fitWidth = this.canvasTarget.clientWidth;
      if (!points.length || this.unfit) return this.landed();

      const bounds = points.reduce(
        (all, at) => all.extend(at),
        new maplibregl.LngLatBounds(points[0], points[0]),
      );
      // One point (or many on one) is no box to frame: it is centred, close in (docs/MAPS.md § The controller).
      const lone = points.every(
        ([lng, lat]) => lng === points[0][0] && lat === points[0][1],
      );
      const maxZoom = lone ? this.constructor.STREET : 14;
      // The card hangs as high as the zoom the fit lands on: ask three times, each with the room of the
      // last answer (a contraction), then fly (docs/MAPS.md § The fit).
      let zoom = this.constructor.STREET;
      const pile = this.biggest();
      for (let ask = 0; ask < 3 && !lone; ask++) {
        zoom =
          this.map.cameraForBounds(bounds, {
            padding: this.room(zoom, pile),
            maxZoom,
          })?.zoom ?? zoom;
      }
      this.hold(() =>
        this.map.fitBounds(bounds, { padding: this.room(zoom, pile), maxZoom }),
      );
      // Lands on the moveend that leaves the map at rest (a resize ends one mid
      // flight), unless a later fit took the waiters (its fitBounds ends this
      // one). A fit onto the view it already has never moves: one frame, then land.
      const fit = this.fitting;
      const land = () => {
        if (this.fitting === fit && this.map?.isMoving()) return;
        this.map?.off("moveend", land);
        if (this.fitting === fit) this.landed();
      };
      this.map.on("moveend", land);
      requestAnimationFrame(() => this.map?.isMoving() || land());
    });
  }

  landed() {
    this.landing.splice(0).forEach((then) => then());
  }

  // A move the controller makes itself (the opening fit, a pan to a row, a
  // resize) is no pan: the frame it came from stays, under whoever has the
  // focus in it. `holding` skips the frame's moveend, which is registered
  // first and so sees the flag before the `once` below clears it. The flag is
  // a token: a hold that interrupts another (Tab down rows whose pins are all
  // off the map) takes the flag over, and the interrupted move's moveend,
  // which fires inside the new move, frees nothing but its own token. A move
  // that never moves fires no moveend: drop the token on the next frame.
  // ponytail: `pad` holds with a plain true and puts back the token it found;
  // it ends its move before it returns, so it needs no token of its own.
  hold(move) {
    const token = (this.holding = Symbol("hold"));
    move?.();
    this.map.once("moveend", () => {
      if (this.holding === token) this.holding = false;
    });
    requestAnimationFrame(() => {
      if (this.holding === token && !this.map?.isMoving()) this.holding = false;
    });
  }

  // One frame for a swapped list: new data, and fit only when the person
  // didn't just pan (that reload would pull the camera back).
  track(target) {
    const point = this.point(target);
    if (!point.geom) return;
    this.pins.set(String(point.id), point);
    this.queuePins();
  }

  queuePins() {
    cancelAnimationFrame(this.syncing);
    this.syncing = requestAnimationFrame(() => {
      const follow = this.fromMap;
      this.fromMap = false;
      this.syncPins();
      this.showPins();
      if (!follow) this.refit();
    });
  }

  syncPins() {
    this.map?.getSource("pins")?.setData(this.collection());
  }

  // Every pin is a point of the glow, weighted by how loud it is. The nights of one point share one
  // point's weight between them: a venue is soft fog, however many nights its grid holds (docs/MAPS.md § A pile).
  collection() {
    const count = new Map();
    for (const { geom } of this.pins.values())
      count.set(String(geom), (count.get(String(geom)) || 0) + 1);
    return {
      type: "FeatureCollection",
      features: [...this.pins.values()].map((point) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: point.geom },
        properties: {
          id: String(point.id),
          weight: this.weigh(point) / count.get(String(point.geom)),
        },
      })),
    };
  }

  // A lone party is an ember, never nothing; the loudest burn. A log of the
  // boost (it runs to the millions), clamped.
  weigh(point) {
    return 0.55 + 0.45 * Math.min(1, Math.log10(1 + (point.boost || 0)) / 6);
  }

  // The Fun clouds (docs/MAPS.md): one heatmap under the labels, over the ground.
  mountClouds() {
    this.map.addSource("pins", { type: "geojson", data: this.collection() });
    this.map.addLayer(this.cloudLayer(), this.fogBefore());
  }

  // Over the ground and its roads, under every label: the first symbol layer no line or fill follows.
  fogBefore() {
    const layers = this.map.getStyle().layers;
    const last = layers.findLastIndex((layer) => layer.type !== "symbol");
    return layers.slice(last + 1).find((layer) => layer.type === "symbol")?.id;
  }

  cloudLayer() {
    return {
      id: "fun-clouds",
      type: "heatmap",
      source: "pins",
      paint: this.glow(),
    };
  }

  // The glow is the theme's own primary, read at runtime (never a hex), from
  // transparent at the edge to the token at full at the core, with a brighter
  // step before it: light on the grey ground, a city from a plane at night.
  // Light needs more of it than dark. Wide and strong enough for neighbours to
  // merge into one blaze; it fades out over the zoom, never a hard cut.
  glow() {
    const [r, g, b] = this.token("--color-primary");
    const dark =
      getComputedStyle(document.documentElement).colorScheme !== "light";
    // tokens:ok the colour is the primary token, read at runtime; `rgba` is only the syntax MapLibre takes.
    const shade = (alpha, white = 0) =>
      `rgba(${[r, g, b].map((c) => Math.round(c + (255 - c) * white)).join(",")},${alpha})`;
    // Dark burns: the core is the token at full, a brighter step before it. Light never reaches full
    // and never brightens (a lighter ring round a darker core is a donut): one soft, long falloff.
    const ramp = dark
      ? [
          0,
          shade(0),
          0.15,
          shade(0.4),
          0.4,
          shade(0.72),
          0.7,
          shade(0.95, 0.35),
          1,
          shade(1),
        ]
      : [
          0,
          shade(0),
          0.1,
          shade(0.14),
          0.3,
          shade(0.32),
          0.55,
          shade(0.52),
          0.8,
          shade(0.7),
          1,
          shade(0.84),
        ];
    // A small box (Depois's strip) has no room for a full cloud: half the radius, or two dozen nights are one slab of gold.
    const reach =
      this.canvasTarget.clientHeight < this.constructor.SMALL ? 0.5 : 1;
    // Lower far out: thirty nights in a metro merge into a blaze round their cards, never one slab (docs/MAPS.md § The cloud).
    const intensity = dark ? [0.8, 1.3, 2.6, 1.8] : [0.7, 1.1, 2.3, 1.6];
    return {
      "heatmap-weight": ["get", "weight"],
      "heatmap-intensity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        6,
        intensity[0],
        10,
        intensity[1],
        12,
        intensity[2],
        15,
        intensity[3],
      ],
      // Pixels: it closes in with the zoom, or a lattice of nights 47px apart is one wash of gold.
      "heatmap-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        6,
        26 * reach,
        10,
        46 * reach,
        12,
        62 * reach,
        15,
        26 * reach,
      ],
      // Capped far out (half at 10, seven tenths at 11), full at 13, gone by 15: the streets show through the metro's core.
      "heatmap-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        8,
        0.45,
        10,
        0.5,
        11,
        0.7,
        13,
        1,
        15,
        0,
      ],
      "heatmap-color": [
        "interpolate",
        ["linear"],
        ["heatmap-density"],
        ...ramp,
      ],
    };
  }

  // A css colour, as [r, g, b]: a canvas turns any of them (oklch included)
  // into the pixels MapLibre can take.
  token(name) {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
    const pad = document
      .createElement("canvas")
      .getContext("2d", { willReadFrequently: true });
    pad.canvas.width = pad.canvas.height = 1;
    pad.fillStyle = value;
    pad.fillRect(0, 0, 1, 1);
    return [...pad.getImageData(0, 0, 1, 1).data].slice(0, 3);
  }

  still() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  // On the map the person sees, and whole: not under a host's sheet (`covered`),
  // and a flyer's width and height clear of the edges, so none is cut by them.
  inView(geom) {
    const { x, y } = this.map.project(geom);
    const box = this.map.getContainer();
    const room = this.inset();
    return (
      x >= room.x &&
      x <= box.clientWidth - room.x &&
      y >= room.top &&
      y <= box.clientHeight - this.covered - room.bottom
    );
  }

  // What a card needs of the box, px from its edges (4px of slack: a fit puts its farthest pins exactly on the
  // line). Its card whole is all a thumb needs: half the big card at each side (the fit's wider room is its
  // own). At street zoom every night on the map is a card, the edge's own: half a card over its point, no more.
  inset() {
    const { CARD, STREET } = this.constructor;
    const zoom = this.map.getZoom();
    const side = Math.ceil((CARD * this.scaleAt(zoom)) / 2);
    if (zoom >= STREET) return { x: 0, top: side - 4, bottom: -4 };
    const { top, bottom } = this.room();
    return { x: side - 4, top: top - 4, bottom: bottom - 4 };
  }

  // The map's own visible radius in km: the circle inscribed in the box less what a card needs (`inset`),
  // round the visible middle. What the host lists is exactly what this draws (docs/MAPS.md § The fit).
  reach() {
    const box = this.map.getContainer();
    const room = this.inset();
    const middle = this.map.getCenter();
    const at = this.map.project(middle);
    const px = Math.max(
      0,
      Math.min(
        box.clientWidth / 2 - room.x,
        (box.clientHeight - this.covered) / 2 - Math.max(room.top, room.bottom),
      ),
    );
    return middle.distanceTo(this.map.unproject([at.x + px, at.y])) / 1000;
  }

  // The room a fit leaves and a thumb needs, from the map's OWN box (docs/MAPS.md § The fit): the card
  // hangs over its point, the label sits under it, the sides are a third of the width at most.
  // `pile` nights of one point stand as a grid wider than a card: the room grows by half that block.
  room(zoom = this.map.getZoom(), pile = 0) {
    const { EDGE, CARD, FOOT } = this.constructor;
    const box = this.canvasTarget;
    const { cols, rows } = this.grid(pile);
    const [across, down] = this.pitch(zoom);
    const side =
      Math.min(EDGE, box.clientWidth / 4) + ((cols - 1) * across) / 2;
    // The side the controls sit on gives up their width and half a card more: the fit never leaves a card in their column.
    const half = Math.ceil((CARD * 5 * this.scaleAt(zoom)) / 14) + 4;
    const taken = this.taken();
    const air = ((rows - 1) * down) / 2;
    return {
      top: Math.min(
        Math.ceil(CARD * this.scaleAt(zoom)) + 4 + air,
        box.clientHeight - FOOT - 16,
      ),
      bottom: FOOT + air,
      left: Math.min(Math.max(side, taken.left + half), box.clientWidth * 0.4),
      right: Math.min(
        Math.max(side, taken.right + half),
        box.clientWidth * 0.4,
      ),
    };
  }

  // The controls over the canvas, as the screen has them (client px): the zoom and expand buttons, the credit,
  // and a host's own (the locator's buttons and its status). Their boxes are no seat for a card or a label.
  controls() {
    return [
      ...this.element.querySelectorAll(
        ".maplibregl-ctrl, [data-locator-target]",
      ),
    ]
      .map((el) => el.getBoundingClientRect())
      .filter((box) => box.width > 0 && box.height > 0);
  }

  // How far the controls of the map's top half reach into each side of the canvas, px: their column. The credit at the
  // foot is no column (the label's room under a card is its own).
  taken() {
    const canvas = this.canvasTarget.getBoundingClientRect();
    const taken = { left: 0, right: 0 };
    for (const box of this.controls()) {
      if (box.top - canvas.top > canvas.height / 2) continue;
      if (box.left + box.width / 2 > canvas.left + canvas.width / 2)
        taken.right = Math.max(taken.right, canvas.right - box.left);
      else taken.left = Math.max(taken.left, box.right - canvas.left);
    }
    return taken;
  }

  // The most nights that share one point: the pile the fit leaves room for.
  biggest() {
    const count = new Map();
    for (const { geom } of this.pins.values())
      if (geom) count.set(String(geom), (count.get(String(geom)) || 0) + 1);
    return Math.max(0, ...count.values());
  }

  // The distance between two seats of a pile's grid, px.
  seat(zoom = this.map.getZoom()) {
    return this.constructor.SEAT * this.constructor.CARD * this.scaleAt(zoom);
  }

  // The steps between two seats of a pile's grid, px: across, the seat; down, the seat and, where the map has
  // room for words (`words`), the label's line, so the loudest's words never lie on the row under it.
  pitch(zoom = this.map.getZoom()) {
    const { LABEL, SMALL } = this.constructor;
    const seat = this.seat(zoom);
    return [seat, seat + (this.canvasTarget.clientHeight < SMALL ? 0 : LABEL)];
  }

  // The grid of a pile of n: its columns follow the map's own shape (a strip is a row, a fifth a column),
  // a row up to three (docs/MAPS.md § A pile).
  grid(n) {
    if (n < 2) return { cols: 1, rows: 1 };
    const box = this.canvasTarget;
    const cols =
      n < 4
        ? n
        : Math.min(
            n,
            Math.ceil(
              Math.sqrt((n * box.clientWidth) / Math.max(1, box.clientHeight)),
            ),
          );
    return { cols, rows: Math.ceil(n / cols) };
  }

  // n seats of the grid, row by row, left to right, in seats from the block's middle: the list's order. A short last
  // row stands on the same columns, from the left.
  seats(n) {
    const { cols, rows } = this.grid(n);
    return Array.from({ length: n }, (_, at) => [
      (at % cols) - (cols - 1) / 2,
      Math.floor(at / cols) - (rows - 1) / 2,
    ]);
  }

  // A thumb's size is the zoom's: one variable on the element, half at FAR and out,
  // full at STREET, a line between. No marker is touched (docs/MAPS.md § Fun clouds).
  scale() {
    if (!this.map) return;
    this.size = this.scaleAt(this.map.getZoom());
    this.element.style.setProperty("--pin-scale", this.size);
  }

  scaleAt(zoom) {
    const { FAR, STREET, SMALL, OUT, FLOOR } = this.constructor;
    if (this.canvasTarget.clientHeight < SMALL) return 0.5;
    if (zoom < FAR) return Math.max(FLOOR, 0.5 - OUT * (FAR - zoom));
    return 0.5 + 0.5 * Math.min(1, (zoom - FAR) / (STREET - FAR));
  }

  // Which pins are thumbs: the first TOP by rank in view; close in (STREET),
  // every one. The rest are the glow only. A thumb stays while its night is in the
  // set; one changing sides shrinks and fades (`leave`, `enter`), it never pops.
  // Thumbs may touch: the z-order is the rank, the loudest on top.
  showPins() {
    if (!this.map) return;
    const { TOP, BIG, STREET } = this.constructor;
    this.scale();
    const street = this.map.getZoom() >= STREET;
    const seen = [...this.pins.values()]
      .filter((point) => this.inView(point.geom))
      .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
    const shown = street ? seen : seen.slice(0, TOP);
    const wanted = new Set(shown.map((point) => String(point.id)));
    // The z-order: a flyer first, then the rank (a stable sort): a paint never buries a real flyer.
    const over = [...shown].sort(
      (a, b) => Boolean(this.flyer(b)) - Boolean(this.flyer(a)),
    );
    shown.forEach((point, rank) => {
      const id = String(point.id);
      let marker = this.markers.get(id);
      if (marker) this.restore(marker);
      else {
        marker = this.leaf(point).addTo(this.map);
        this.markers.set(id, marker);
        this.enter(marker);
      }
      marker.setLngLat(point.geom);
      const el = marker.getElement();
      el.toggleAttribute("data-first", rank < BIG);
      el.dataset.base = String(shown.length - over.indexOf(point));
      el.dataset.z = el.dataset.base;
      el.style.zIndex = el.dataset.z;
    });
    for (const id of [...this.markers.keys()])
      if (!wanted.has(id)) this.leave(id);
    this.lay();
  }

  enter(marker) {
    if (this.still()) return;
    const el = marker.getElement();
    this.fade(el, marker, "0", "0.4");
    el.getBoundingClientRect();
    requestAnimationFrame(() => this.fade(el, marker, "1", ""));
  }

  // The transition is on for the fade only: a pin that is back keeps none of it.
  fade(el, marker, opacity, scale) {
    const { FADE } = this.constructor;
    clearTimeout(el.fading);
    el.style.transition = `opacity ${FADE}ms, scale ${FADE}ms`;
    el.style.scale = scale;
    marker.setOpacity(opacity);
    if (opacity === "1")
      el.fading = setTimeout(() => (el.style.transition = ""), FADE);
  }

  restore(marker) {
    const el = marker.getElement();
    if (!el.dataset.leaving) return;
    delete el.dataset.leaving;
    this.fade(el, marker, "1", "");
  }

  leave(id) {
    const marker = this.markers.get(id);
    const el = marker.getElement();
    if (el.dataset.leaving) return;
    if (this.still()) return this.drop(id);
    const token = (this.leaving = (this.leaving || 0) + 1);
    el.dataset.leaving = String(token);
    this.fade(el, marker, "0", "0.4");
    setTimeout(
      () => el.dataset.leaving === String(token) && this.drop(id),
      this.constructor.FADE,
    );
  }

  // A removed node gets no mouseleave or blur: what the pointer or the focus lit goes with it.
  drop(id) {
    const marker = this.markers.get(id);
    if (marker?.getElement().matches(":hover, :focus")) {
      this.lit?.remove();
      this.row(id)?.classList.remove("bg-base-200");
    }
    marker?.remove();
    this.markers.delete(id);
    this.lay();
  }

  // A row whose party is only glow: its spot lights, a ring at the point.
  ember(point, on) {
    this.spot?.remove();
    this.spot = null;
    if (!on || !point.geom || !this.map) return;
    const el = document.createElement("span");
    el.dataset.ember = "1";
    el.className =
      "block size-8 rounded-full bg-primary/40 ring-2 ring-primary shadow-lg";
    this.spot = new maplibregl.Marker({ element: el, anchor: "center" })
      .setLngLat(point.geom)
      .addTo(this.map);
  }

  // The classic green sweep (a bar from the visible middle, one clockwise
  // turn, a faint tail trailing, a few range rings), over the map and under
  // the pins, then gone. The keyframes are `animate-hunt` (the app's css).
  sweepOnce() {
    const box = this.map.getContainer();
    const w = box.clientWidth;
    const h = box.clientHeight - this.covered;
    const el = document.createElement("div");
    el.setAttribute("aria-hidden", "true");
    el.dataset.sweep = "1";
    el.className = [
      "absolute -translate-1/2 aspect-square rounded-full pointer-events-none motion-reduce:hidden animate-hunt",
      "bg-[conic-gradient(from_0deg,transparent_0deg,transparent_250deg,color-mix(in_oklab,var(--color-success)_22%,transparent)_356deg,var(--color-success)_359.4deg,transparent_360deg),repeating-radial-gradient(circle,transparent_0_calc(12rem-1px),color-mix(in_oklab,var(--color-success)_35%,transparent)_calc(12rem-1px)_12rem)]",
    ].join(" ");
    el.style.cssText = `left:${w / 2}px;top:${h / 2}px;width:${Math.hypot(w, h) * 2}px`;
    el.addEventListener("animationend", () => el.remove());
    // Right after the canvas: the markers are added after it, so they sit over.
    this.map.getCanvas().after(el);
  }

  // A night is a thumb: its flyer, else the picture its source hands over (`stand`), else today's
  // name pill. A bar (`at` is its name) is its plain point with its name as the label.
  leaf(point) {
    const night = point.at && point.at !== point.name;
    const flyer = this.flyer(point);
    const stand = night && !flyer ? this.stand(point.id) : null;
    const thumb = flyer || stand;
    const bar = point.at && !night;
    const element = thumb
      ? this.thumbPin(point, stand)
      : bar
        ? this.barPin(point)
        : point.at
          ? this.labelPin(point)
          : this.pin(point);
    const marker = new maplibregl.Marker({
      element,
      anchor: thumb || (point.at && !bar) ? "bottom" : "center",
    }).setLngLat(point.geom);
    if (!point.at)
      marker.setPopup(
        new maplibregl.Popup({ offset: 28 }).setHTML(this.popup(point)),
      );
    return marker;
  }

  // The flyer a night wears, if it has one: a bar's `cover` is its dot's, never a flyer.
  flyer(point) {
    return point.at && point.at !== point.name && (point.poster || point.cover);
  }

  // The picture a pin's source (its row, or its hidden span) hands over for a night with no flyer:
  // one root, cloned out of its `template[data-map-thumb]`, never moved (docs/MAPS.md § Fun clouds).
  stand(id) {
    return this.row(id)
      ?.querySelector("template[data-map-thumb]")
      ?.content.firstElementChild?.cloneNode(true);
  }

  // Parties that share a point are a pile: a grid centred on that point, the loudest in the middle. Nothing else
  // is: two piles, or a pile and a thumb, never merge, so a card stays at its own place (docs/MAPS.md § A pile).
  // Every card of a grid wears the default size (the big three keep their rank, never a bigger card inside a grid)
  // and stays on its seat. Then `relax` seats what is left, by the least.
  fan() {
    const live = [...this.markers].filter(
      ([id, marker]) =>
        this.pins.get(id)?.geom && !marker.getElement().dataset.leaving,
    );
    live.sort(
      ([a], [b]) =>
        (this.pins.get(a).rank ?? 1e9) - (this.pins.get(b).rank ?? 1e9),
    );
    const piles = [];
    for (const [id] of live) {
      const at = this.map.project(this.pins.get(id).geom);
      const pile = piles.find((one) =>
        one.some(
          (other) => Math.hypot(other.at.x - at.x, other.at.y - at.y) <= 0.5,
        ),
      );
      if (pile) pile.push({ id, at });
      else piles.push([{ id, at }]);
    }
    // The list's order: the host's `pin` targets, in the page (time, then rank), is where card n sits in a grid.
    const listed = new Map(
      this.pinTargets.map((target, at) => [String(this.point(target).id), at]),
    );
    const fixed = new Set();
    for (const pile of piles) {
      const lone = pile.length < 2;
      const seats = lone ? [null] : this.seats(pile.length);
      const cells = lone
        ? pile
        : [...pile].sort((a, b) => listed.get(a.id) - listed.get(b.id));
      pile.forEach(({ id }, rank) => {
        const el = this.markers.get(id).getElement();
        el.toggleAttribute("data-big", lone && el.hasAttribute("data-first"));
        this.pose(id, seats[cells.findIndex((cell) => cell.id === id)], rank);
        if (!lone) fixed.add(id);
      });
    }
    this.relax(
      live
        .filter(([, marker]) => marker.getElement().dataset.thumb)
        .map(([id]) => id),
      fixed,
    );
  }

  // The cards go in the order of the z, the loudest first, and each takes the nearest seat (`place`): the cards over
  // keep their place, the quieter yields. A grid's cards (`fixed`) stay on their seats and are only in the way.
  // The first card whose label stands whole says its words (`voice`) and every card after it keeps off them.
  relax(ids, fixed) {
    const { AIR } = this.constructor;
    const canvas = this.canvasTarget;
    const room = {
      w: canvas.clientWidth,
      h: canvas.clientHeight - this.covered,
    };
    const edge = canvas.getBoundingClientRect();
    const stops = this.controls().map((box) =>
      this.grow(
        {
          left: box.left - edge.left,
          right: box.right - edge.left,
          top: box.top - edge.top,
          bottom: box.bottom - edge.top,
        },
        AIR,
      ),
    );
    const { CARD } = this.constructor;
    const items = ids
      .map((id) => {
        const marker = this.markers.get(id);
        const el = marker.getElement();
        const home = this.map.project(this.pins.get(id).geom);
        const { x, y } = marker.getOffset();
        // A card is square, anchored at its bottom centre: the big one 7rem, the rest 5rem.
        const w =
          CARD * (el.hasAttribute("data-big") ? 1 : 5 / 7) * (this.size ?? 1);
        return {
          id,
          marker,
          home,
          x: home.x + x,
          y: home.y + y,
          w,
          big: el.hasAttribute("data-big"),
          z: Number(el.dataset.base),
        };
      })
      .sort((a, b) => b.z - a.z);
    const over = [];
    let words = null;
    for (const [at, card] of items.entries()) {
      const rest = items.slice(at + 1);
      if (!fixed.has(card.id))
        this.place(
          card,
          over,
          rest,
          words,
          stops,
          room,
          rest.filter((one) => fixed.has(one.id)),
        );
      words ||= this.voice(card, stops, room);
      over.push(card);
    }
    for (const { marker, home, x, y } of items)
      marker.setOffset([x - home.x, y - home.y]);
    this.unbury(items);
  }

  // No night under a bigger card (docs/MAPS.md § Crowding): a card that still shows less than HALF of itself, painted, could
  // not be parted from the cards over it by any seat inside the leash. Its z goes just over the cover where it and its
  // neighbours show the most, when that shows more than it does: the smaller is painted on top of the bigger, a crowd of
  // one size is dealt so that every card shows its half. A card moves once. The z's are the same set, dealt in the new order.
  unbury(items) {
    const { RING } = this.constructor;
    const spots = Array.from({ length: 8 }, (_, at) => (at + 0.5) / 8);
    const box = (card, air = 0) => ({
      left: card.x - card.w / 2 - air,
      right: card.x + card.w / 2 + air,
      top: card.y - card.w - air,
      bottom: card.y + air,
    });
    const touch = (a, b) => this.meet(box(a, RING), box(b, RING));
    const order = [...items];
    const above = (card) =>
      order.slice(0, order.indexOf(card)).filter((one) => touch(one, card));
    const shows = (card) => {
      const cover = above(card).map((one) => box(one, RING));
      let seen = 0;
      for (const sx of spots) {
        for (const sy of spots) {
          const [px, py] = [
            card.x + (sx - 0.5) * card.w,
            card.y - card.w + sy * card.w,
          ];
          if (
            !cover.some(
              (one) =>
                px >= one.left &&
                px <= one.right &&
                py >= one.top &&
                py <= one.bottom,
            )
          )
            seen++;
        }
      }
      return seen / spots.length ** 2;
    };
    const HALF = 0.55;
    const done = new Set();
    for (let pass = 0; pass < items.length; pass++) {
      let worst = null;
      for (const card of order) {
        if (done.has(card)) continue;
        const share = shows(card);
        if (share < HALF && (!worst || share < worst.share))
          worst = { card, share };
      }
      if (!worst) break;
      const { card } = worst;
      done.add(card);
      const group = [
        card,
        ...order.filter((one) => one !== card && touch(one, card)),
      ];
      const lack = () =>
        group.reduce((sum, one) => sum + Math.max(0, HALF - shows(one)), 0);
      const at = order.indexOf(card);
      let best = { lack: lack(), over: null };
      for (const over of order.slice(0, at).filter((one) => touch(one, card))) {
        order.splice(at, 1);
        order.splice(order.indexOf(over), 0, card);
        const next = lack();
        order.splice(order.indexOf(card), 1);
        order.splice(at, 0, card);
        if (next < best.lack - 0.01) best = { lack: next, over };
      }
      if (!best.over) continue;
      order.splice(at, 1);
      order.splice(order.indexOf(best.over), 0, card);
    }
    const stack = items.map((card) => card.z);
    for (const [at, { marker, z }] of order.entries()) {
      if (stack[at] === z) continue;
      const el = marker.getElement();
      el.dataset.z = String(stack[at]);
      if (!el.hasAttribute("data-lit")) el.style.zIndex = el.dataset.z;
    }
  }

  // The nearest seat for a card, on rings round its point out to the leash (a seat's middle within LEASH widths of the
  // point): the first where at most MOST of its box (sampled, 8 x 8) is under the cards over it, where it touches no
  // control and no word, and where it spills off the map no more than its own point does. Where there is none (a crowd
  // far out), the one with the least under it: the cards fan as a deck, each edge showing (docs/MAPS.md § A pile).
  place(card, over, rest, words, stops, room, held) {
    const {
      MOST: LOOSE,
      TIGHT,
      SOME,
      LEASH,
      RINGS,
      TURNS,
      STREET,
      AIR,
      RING,
    } = this.constructor;
    const street = this.map.getZoom() >= STREET;
    const MOST = street ? LOOSE : TIGHT;
    const { w } = card;
    const spots = Array.from({ length: 8 }, (_, at) => (at + 0.5) / 8);
    const reach = LEASH * w;
    const near = over.filter(
      (one) =>
        Math.abs(one.x - card.x) <= reach + (one.w + w) / 2 &&
        Math.abs(one.y - one.w / 2 - (card.y - w / 2)) <=
          reach + (one.w + w) / 2,
    );
    const spill = (x, y) =>
      Math.max(0, w / 2 + AIR - x) +
      Math.max(0, x + w / 2 + AIR - room.w) +
      Math.max(0, w - y) +
      Math.max(0, y - room.h);
    const lost = spill(card.x, card.y);
    const boxOf = (x, y) => ({
      left: x - w / 2,
      right: x + w / 2,
      top: y - w,
      bottom: y,
    });
    const spot = (one) => ({
      left: one.x - one.w / 2,
      right: one.x + one.w / 2,
      top: one.y - one.w,
      bottom: one.y,
    });
    // The grid's cards this one could lie on, with the cards already over them (placed before this one), painted.
    const guard = held.map((one) => ({
      one,
      cover: over
        .filter((c) => this.meet(this.grow(spot(c), RING), spot(one)))
        .map((c) => this.grow(spot(c), RING)),
    }));
    const cost = (x, y) => {
      const box = boxOf(x, y);
      let bad = 0;
      if (stops.some((stop) => this.meet(box, stop))) bad++;
      // Off the map more than its own point is: no seat. Off it at all, below STREET: the second choice (a card slides in
      // from the edge; at street zoom a night on the edge keeps its card half over it).
      if (spill(x, y) > lost + 0.5) bad++;
      else if (!street && spill(x, y) > 0.5) bad += 0.1;
      // The big three never cover each other below STREET: a big card keeps clear of every big card over it.
      if (
        !street &&
        card.big &&
        over.some(
          (one) =>
            one.big &&
            this.meet(box, {
              left: one.x - one.w / 2,
              right: one.x + one.w / 2,
              top: one.y - one.w,
              bottom: one.y,
            }),
        )
      )
        bad++;
      let hits = 0;
      for (const sx of spots) {
        for (const sy of spots) {
          const [px, py] = [x + (sx - 0.5) * w, y - w + sy * w];
          if (
            near.some(
              (one) =>
                Math.abs(px - one.x) <= one.w / 2 + RING &&
                py <= one.y + RING &&
                py >= one.y - one.w - RING,
            )
          )
            hits++;
        }
      }
      // The loudest's words give way before a card does: half of every card shows first (MOST is the aim, a card
      // more under than SOME is the worst), then its words, then the aim.
      const under = hits / spots.length ** 2;
      const lies = words && this.meet(box, words) ? 0.5 : 0;
      // A grid's card cannot move: this card is the one that keeps off it. What shows of it is what the cards over it and
      // this one leave, painted (RING).
      let on = 0;
      for (const { one, cover } of guard) {
        if (!this.meet(this.grow(boxOf(x, y), RING), spot(one))) continue;
        const mine = this.grow(boxOf(x, y), RING);
        let covered = 0;
        for (const sx of spots) {
          for (const sy of spots) {
            const [px, py] = [
              one.x + (sx - 0.5) * one.w,
              one.y - one.w + sy * one.w,
            ];
            if (
              (px >= mine.left &&
                px <= mine.right &&
                py >= mine.top &&
                py <= mine.bottom) ||
              cover.some(
                (c) =>
                  px >= c.left &&
                  px <= c.right &&
                  py >= c.top &&
                  py <= c.bottom,
              )
            )
              covered++;
          }
        }
        on = Math.max(on, covered / spots.length ** 2);
      }
      return (
        bad * 10 +
        (Math.max(under, on) > SOME ? 2 + Math.max(under, on) : 0) +
        lies +
        Math.max(0, under - MOST) +
        Math.max(0, on - MOST)
      );
    };
    // A card that has to move leaves the most room for the quieter ones still to come: among the seats of the first
    // ring that holds one and the two after it, the one that lies least on their places (`rest`, at their points).
    const crowd = (x, y) =>
      rest.reduce((sum, one) => {
        const across =
          Math.min(x + w / 2, one.x + one.w / 2) -
          Math.max(x - w / 2, one.x - one.w / 2);
        const down = Math.min(y, one.y) - Math.max(y - w, one.y - one.w);
        return across > 0 && down > 0
          ? sum + (across * down) / (one.w * one.w)
          : sum;
      }, 0);
    let best = { cost: cost(card.x, card.y), x: card.x, y: card.y };
    let pick = null;
    let first = null;
    // Rings round where the card hangs from its point; a seat is only one whose middle is within LEASH widths of the
    // point itself (the card's middle is half its width above it at home).
    for (let ring = 1; ring <= RINGS && best.cost > 0; ring++) {
      if (first !== null && ring > first + 2) break;
      const far = (ring / RINGS) * (LEASH + 0.5) * w;
      for (let turn = 0; turn < TURNS; turn++) {
        const angle = (turn / TURNS) * 2 * Math.PI;
        const [x, y] = [
          card.x + far * Math.cos(angle),
          card.y + far * Math.sin(angle),
        ];
        if (Math.hypot(x - card.home.x, y - w / 2 - card.home.y) > reach)
          continue;
        const next = cost(x, y);
        if (next > 0) {
          if (first === null && next < best.cost) best = { cost: next, x, y };
          continue;
        }
        first ??= ring;
        const score = crowd(x, y) + ring * 0.01;
        if (!pick || score < pick.score) pick = { score, x, y };
      }
    }
    if (pick) best = pick;
    card.x = best.x;
    card.y = best.y;
  }

  // The words every card keeps off, in the canvas's own px, with air: the first card, loudest first, whose label stands
  // whole where the card sits (on the canvas, off the controls). A small box (the 20dvh strip) has no room under its
  // cards for words: none, they wait hidden where they would touch (docs/MAPS.md § Fun clouds).
  voice(card, stops, room) {
    const { SMALL, HANG, MARGIN } = this.constructor;
    if (this.canvasTarget.clientHeight < SMALL) return null;
    const label = card.marker.getElement().querySelector("[data-label]");
    if (!label) return null;
    const [w, h] = [label.offsetWidth, label.offsetHeight];
    const found = {
      left: card.x - w / 2,
      right: card.x + w / 2,
      top: card.y,
      bottom: card.y + HANG + h,
    };
    const whole =
      found.left >= MARGIN &&
      found.right <= room.w - MARGIN &&
      found.bottom <= room.h - MARGIN &&
      !stops.some((stop) => this.meet(found, stop));
    return whole ? this.halo(found) : null;
  }

  // A label's air: NEAR at its sides and under it, none over it (what is over a label is the foot of its own card, the
  // HANG away, and of a row's neighbours at the same foot).
  halo(box) {
    const { NEAR } = this.constructor;
    return {
      left: box.left - NEAR,
      right: box.right + NEAR,
      top: box.top,
      bottom: box.bottom + NEAR,
    };
  }

  // A box with air round it.
  grow(box, air) {
    return {
      left: box.left - air,
      right: box.right + air,
      top: box.top - air,
      bottom: box.bottom + air,
    };
  }

  // `seat` null sits on its point; else a cell of the pile's grid (in seats), the scale on top.
  pose(id, seat, rank = 0) {
    const marker = this.markers.get(id);
    if (!marker) return;
    const el = marker.getElement();
    if (!seat) {
      marker.setOffset([0, 0]);
      return;
    }
    // Name pills stack, loudest at the point: side by side their words would cover each other.
    if (el.classList.contains("badge")) {
      marker.setOffset([0, rank * -28]);
      return;
    }
    const [across, down] = this.pitch();
    marker.setOffset([seat[0] * across, seat[1] * down]);
  }

  point(target) {
    return JSON.parse(target.dataset.point);
  }

  erase(target) {
    const { id } = this.point(target);
    this.markers.get(id)?.remove();
    this.markers.delete(id);
  }

  draw(target, bubble) {
    const point = this.point(target);
    if (this.markers.has(point.id) || !point.geom) return;

    // A pin with an `at` is its label; the popup opens on hover.
    const label = !bubble && point.at;
    const element = bubble
      ? this.bubble(point)
      : label
        ? this.labelPin(point)
        : this.pin(point);
    const marker = new maplibregl.Marker({
      element,
      anchor: label ? "bottom" : "center",
    }).setLngLat(point.geom);
    if (!bubble && !label)
      marker.setPopup(
        new maplibregl.Popup({ offset: 28 }).setHTML(this.popup(point)),
      );
    this.markers.set(point.id, marker.addTo(this.map));
    if (bubble) this.spread();
  }

  // Bubbles never sit under one another, and only those the map shows are Tab
  // stops (docs/MAPS.md § Spots). Offsets are screen px from the point, so
  // zoomed in they settle back on it.
  spread() {
    if (!this.map) return;
    const bounds = this.map.getBounds();
    const all = [...this.markers.values()]
      .filter((marker) => marker.getElement().dataset.spot)
      .map((marker) => {
        const el = marker.getElement();
        const { x, y } = this.map.project(marker.getLngLat());
        return { marker, el, x, y, r: el.offsetWidth / 2 + 2 };
      });
    for (let pass = 0; pass < 8; pass++) {
      for (let i = 0; i < all.length; i++) {
        for (let j = i + 1; j < all.length; j++) {
          const [a, b] = [all[i], all[j]];
          let [dx, dy] = [b.x - a.x, b.y - a.y];
          let gap = Math.hypot(dx, dy);
          const least = a.r + b.r;
          if (gap >= least) continue;
          if (!gap) [dx, dy, gap] = [1, 0, 1];
          const push = (least - gap) / 2;
          a.x -= (push * dx) / gap;
          a.y -= (push * dy) / gap;
          b.x += (push * dx) / gap;
          b.y += (push * dy) / gap;
        }
      }
    }
    for (const one of all) {
      const home = this.map.project(one.marker.getLngLat());
      one.marker.setOffset([one.x - home.x, one.y - home.y]);
      if (bounds.contains(one.marker.getLngLat()))
        one.el.removeAttribute("tabindex");
      else one.el.tabIndex = -1;
    }
  }

  // A night's thumb: the card (its flyer, else the host's stand-in) and, under it, the label. Fire owns
  // the card (size, ring, shadow); the host owns only what fills the square. The thumb is the card's
  // box, the label hangs from it and never makes it taller. A flyer or a stand-in is decoration:
  // the button's name is the label's whole text.
  thumbPin(point, stand) {
    const el = document.createElement("button");
    el.type = "button";
    el.dataset.thumb = "1";
    el.className =
      "relative block cursor-pointer origin-bottom transition-[scale] duration-200";
    el.title = point.name;
    el.setAttribute("aria-label", this.said(point));

    const card = document.createElement("span");
    card.className = `block size-[calc(5rem*var(--pin-scale,1))] in-data-[big]:size-[calc(7rem*var(--pin-scale,1))] overflow-hidden rounded-field bg-base-100 bg-cover bg-center shadow-2xl *:size-full ${point.soon ? "ring-2 ring-secondary" : "ring-1 ring-base-100"}`;
    if (stand) card.append(stand);
    else card.style.backgroundImage = `url("${point.poster || point.cover}")`;
    card.setAttribute("aria-hidden", "true");

    el.append(this.label(el, point), card);
    this.bindPin(el, point);
    return el;
  }

  // A plain point (no `at`): the ringed dot, its popup opens on click. A bar is the same dot plus a label.
  pin(point) {
    const el = document.createElement("span");
    el.className = `block size-4 cursor-pointer rounded-full bg-base-300 bg-cover bg-center shadow-md ring-2 ${point.soon ? "ring-secondary" : "ring-base-100"}`;
    if (point.cover) el.style.backgroundImage = `url("${point.cover}")`;
    el.title = point.name;
    return el;
  }

  // A bar: its plain point, ringed in the "now" colour while open, and its name for the label.
  barPin(point) {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "relative block cursor-pointer";
    el.title = point.name;
    el.setAttribute("aria-label", point.name);

    const dot = document.createElement("span");
    dot.className = `block size-4 rounded-full bg-base-300 bg-cover bg-center shadow-md ring-2 ${point.soon ? "ring-secondary" : "ring-base-100"}`;
    if (point.cover) dot.style.backgroundImage = `url("${point.cover}")`;
    dot.setAttribute("aria-hidden", "true");

    el.append(this.label(el, point), dot);
    this.bindPin(el, point);
    return el;
  }

  // The hour and the name, one line: what a thumb says and its button is named by.
  said(point) {
    return point.at.includes(point.name)
      ? point.at
      : `${point.at} · ${point.name}`;
  }

  // One line under its marker, 12rem at most, never in the marker's box. It starts hidden: `labels` gives it room.
  label(el, point) {
    const label = document.createElement("span");
    label.dataset.label = "1";
    label.setAttribute("aria-hidden", "true");
    label.className = `pointer-events-none absolute top-full left-1/2 mt-1 w-max max-w-48 -translate-x-1/2 truncate rounded-field px-1.5 text-xs leading-5 font-semibold tabular-nums shadow-md transition-opacity duration-150 motion-reduce:transition-none data-off:opacity-0 ${point.soon ? "bg-secondary text-secondary-content" : "bg-base-100 text-base-content"}`;
    label.textContent = this.said(point);
    label.toggleAttribute("data-off", true);
    el.toggleAttribute("data-hid", true);
    return label;
  }

  // Placed by rank, the top three first: a label shows only where its box touches no label already
  // placed and no louder marker (never under a thumb's edge); the rest wait hidden, their thumbs
  // stay (docs/MAPS.md § Fun clouds). Run on every zoom and every change of the set. A marker still
  // fading in is not its size yet: its label waits, and the check runs again until it has it.
  labels() {
    if (!this.map) return;
    // The label of what is on top is placed first: the z-order is the rank, a flyer first.
    const canvas = this.canvasTarget.getBoundingClientRect();
    const { MARGIN } = this.constructor;
    const inside = (box) =>
      box.left >= canvas.left + MARGIN &&
      box.right <= canvas.right - MARGIN &&
      box.top >= canvas.top + MARGIN &&
      box.bottom <= canvas.bottom - this.covered - MARGIN;
    const placed = [];
    const louder = [];
    const stops = this.controls();
    let growing = false;
    const ordered = [...this.markers].filter(
      ([, marker]) => !marker.getElement().dataset.leaving,
    );
    ordered.sort(
      ([, a], [, b]) =>
        Number(b.getElement().dataset.z) - Number(a.getElement().dataset.z),
    );
    const cards = ordered.map(([, marker]) =>
      marker.getElement().getBoundingClientRect(),
    );
    // A bar's plain point is no card: only a thumb lies under a label's no-go.
    const thumbs = ordered.map(([, marker]) =>
      Boolean(marker.getElement().dataset.thumb),
    );
    for (const [at, [, marker]] of ordered.entries()) {
      const el = marker.getElement();
      const own = cards[at];
      if (el.querySelector("[data-label]")) {
        const box = el.querySelector("[data-label]").getBoundingClientRect();
        const fading = Boolean(el.style.transition);
        growing ||= fading;
        // Its own thumb must show: one more than half under a louder one says nothing, the louder's words win.
        const buried = louder.some((one) => this.under(own, one) > 0.5);
        // No label lies on a card, louder or quieter: `relax` keeps the loudest's clear where the map has room.
        // Nor on a control, and never touching a card's edge: the card's ring and shadow have air round them.
        const room =
          !fading &&
          !buried &&
          inside(box) &&
          !stops.some((stop) => this.meet(box, stop)) &&
          !placed.some((one) => this.meet(box, one)) &&
          !cards.some(
            (card, other) =>
              other !== at && thumbs[other] && this.meet(this.halo(box), card),
          );
        el.toggleAttribute("data-hid", !room);
        if (room) placed.push(box);
        this.paint(el);
      }
      louder.push(own);
    }
    clearTimeout(this.relay);
    if (growing) this.relay = setTimeout(() => this.labels(), 60);
  }

  // The share of `a` that `b` lies over.
  under(a, b) {
    const across = Math.min(a.right, b.right) - Math.max(a.left, b.left);
    const down = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
    return across > 0 && down > 0
      ? (across * down) / ((a.right - a.left) * (a.bottom - a.top))
      : 0;
  }

  meet(a, b) {
    return (
      a.left < b.right &&
      b.left < a.right &&
      a.top < b.bottom &&
      b.top < a.bottom
    );
  }

  // A label is shown where it has room, or while its marker is lit.
  paint(el) {
    el.querySelector("[data-label]")?.toggleAttribute(
      "data-off",
      el.hasAttribute("data-hid") && !el.hasAttribute("data-lit"),
    );
  }

  // Everything that depends on where the markers stand: the fan, then the labels.
  lay() {
    this.fan();
    this.labels();
  }

  // A pin by its label (an hour, or its own name): a small pill, the popup
  // above it only while lit. Clicking it finds its row, or opens the link
  // when the list is hidden.
  labelPin(point) {
    const el = document.createElement("button");
    el.type = "button";
    el.className = `badge badge-sm block max-w-48 truncate h-auto min-h-5 py-0.5 font-semibold tabular-nums shadow-md cursor-pointer ${point.soon ? "badge-secondary" : "border-base-content/20 bg-base-100"}`;
    el.setAttribute("aria-label", point.at); // a pill names its place already
    el.textContent = point.at;
    this.bindPin(el, point);
    return el;
  }

  bindPin(el, point) {
    for (const [type, on] of [
      ["mouseenter", true],
      ["mouseleave", false],
      ["focus", true],
      ["blur", false],
    ]) {
      el.addEventListener(type, () => this.light(point, on));
    }
    el.addEventListener("click", () => {
      const row = this.row(point.id);
      if (!row?.checkVisibility()) return Turbo.visit(point.link);
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
      row.querySelector("a")?.focus({ preventScroll: true });
    });
  }

  // Hovering or tabbing a row lights its pin, panning to it if it is off the
  // map; the pin lights its row back.
  spotlight({ currentTarget, type }) {
    const point = this.point(currentTarget);
    const on = type === "mouseenter" || type === "focusin";
    this.light(point, on);
    if (
      on &&
      point.geom &&
      this.map &&
      !this.map.getBounds().contains(point.geom)
    ) {
      this.hold(() => this.map.panTo(point.geom));
    }
  }

  light(point, on) {
    // The ring is the hover's: it goes with it, though the pan promoted the party to a pin meanwhile.
    if (!on) this.ember(point, false);
    const el = this.markers.get(point.id)?.getElement();
    // Only glow: no marker, so the spot lights (a row is never dead).
    if (this.cloudsValue && (!el || el.dataset.leaving)) {
      this.ember(point, on);
      if (point.at) this.hover(point, on);
    } else if (el) {
      el.toggleAttribute("data-lit", on);
      this.paint(el);
      el.style.zIndex = on ? 900 : el.dataset.z || "";
      if (el.classList.contains("origin-bottom"))
        el.classList.toggle("scale-110", on);
      if (point.at) this.hover(point, on);
      else {
        el.classList.toggle("scale-125", on);
        el.classList.toggle("ring-primary", on);
      }
    }
    this.row(point.id)?.classList.toggle("bg-base-200", on);
  }

  // One popup for whichever is lit; no close button, the mouse leaving is
  // the close. It takes no focus (its link would wear the focus ring) and its
  // tip stays centred: above the pill, or below it when the pill is too near
  // the top. MapLibre's own pick slides to a corner.
  hover(point, on) {
    this.lit?.remove();
    if (!on || !this.map) return;
    const marker = this.markers.get(point.id);
    const thumb = marker?.getElement().dataset.thumb;
    this.lit = new maplibregl.Popup({
      ...this.above(
        point,
        thumb ? -(marker.getElement().offsetHeight || 64) : -26,
        marker?.getOffset(),
      ),
      closeButton: false,
      closeOnClick: false,
      focusAfterOpen: false,
    })
      .setLngLat(point.geom)
      .setHTML(this.popup(point))
      .addTo(this.map);
    // Over every thumb, whatever their rank.
    this.lit.getElement().style.zIndex = 1000;
  }

  // Above the pill (`up` px, its height), or below it when the pill is too
  // near the top: either way the tip stays centred. The marker's own offset
  // rides along: a fanned flyer stands off its point (fan).
  above(point, up, { x = 0, y: dy = 0 } = {}) {
    const y = this.map.project(point.geom).y + dy;
    const anchor = y < 280 ? "top" : "bottom";
    return { anchor, offset: { top: [x, dy + 8], bottom: [x, dy + up] } };
  }

  row(id) {
    return this.pinTargets.find((one) => this.point(one).id === id);
  }

  // A city or a venue: how many are inside it, sized by how many. Clicking
  // it is the zoom to the scale the server named, or, with a `visit`, that
  // page (um's /doar: a town's list).
  bubble(spot) {
    const size = Math.min(56, 28 + String(spot.count).length * 8);
    const el = document.createElement("button");
    el.type = "button";
    el.className =
      "flex items-center justify-center rounded-full bg-primary text-primary-content text-sm font-semibold tabular-nums shadow-md ring-2 ring-base-100 leading-none cursor-pointer";
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.innerHTML = `<span>${spot.count}</span>`;
    el.title = spot.name;
    // The count is the visible text, so the name holds it (WCAG 2.5.3).
    el.setAttribute("aria-label", `${spot.name} · ${spot.count}`);
    el.dataset.spot = "1";
    el.addEventListener("click", () =>
      spot.visit
        ? Turbo.visit(spot.visit)
        : this.map.flyTo({ center: spot.geom, zoom: spot.zoom || 14 }),
    );
    return el;
  }

  // Panning is continuous and each move is a request, so it waits for a
  // pause. Everything else in the query string is a filter somebody chose —
  // it survives the pan; only where and how close is ours to rewrite.
  refresh(wait = 500) {
    clearTimeout(this.pending);
    this.pending = setTimeout(() => {
      if (!this.map) return;
      // The reload that follows is the pan's, so it must not refit.
      this.fromMap = true;
      const { lng, lat } = this.map.getCenter();
      const url = new URL(window.location.href);
      url.searchParams.set("lng", lng.toFixed(3));
      url.searchParams.set("lat", lat.toFixed(3));
      url.searchParams.set("zoom", Math.round(this.map.getZoom()));
      // The map's own visible radius: the host lists exactly what this draws (docs/MAPS.md § The fit).
      url.searchParams.set("km", this.reach().toFixed(2));
      Turbo.visit(url.toString(), {
        action: "replace",
        frame: this.frameValue,
      });
    }, wait);
  }

  // Every word here is somebody's record (a promoter names a Fun): escaped,
  // so a name with markup in it reads as those characters (docs/MAPS.md).
  popup(point) {
    return `
      <a href="${esc(point.link)}" class="flex w-56 flex-col gap-2" target="_blank" rel="noopener noreferrer">
        ${point.cover ? `<img src="${esc(point.cover)}" alt="${esc(point.name)}" class="aspect-video w-full object-cover rounded-field">` : ""}
        <div>
          <h3 class="font-semibold text-base leading-snug">${esc(point.name)}</h3>
          <p class="text-sm text-base-content/70">${esc(point.day)}</p>
          ${point.value ? `<p class="text-sm text-base-content/70">${esc(point.value)}</p>` : ""}
        </div>
      </a>
    `;
  }

  async locate() {
    if (!navigator.geolocation) return this.constructor.NOWHERE;

    try {
      const at = await new Promise((ok, no) =>
        navigator.geolocation.getCurrentPosition(ok, no),
      );
      return [at.coords.longitude, at.coords.latitude];
    } catch {
      return this.constructor.NOWHERE;
    }
  }
}
